import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  COLUMN_TYPES,
  MAX_COLUMNS,
  MAX_LABEL_LENGTH,
  MAX_NOTE_LENGTH,
  type ColumnDraft,
  type ColumnDraftErrors,
} from "@/features/data-curation/schemaDraft";
import type { CurationValueType } from "@/features/data-curation/types";
import { cn } from "@/utils/cn";
import { Plus, Trash2 } from "lucide-react";
import { useId } from "react";

export const SELECT_CLASS =
  "h-8 rounded-[3px] border border-zinc-300 bg-white px-2 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900 disabled:cursor-not-allowed disabled:opacity-50";
export const textareaClass = (invalid = false) =>
  cn(
    "mt-1 block w-full min-w-64 resize-y rounded-[3px] border bg-white px-3 py-1.5 text-sm font-normal text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50",
    invalid ? "border-red-700 focus:ring-red-700" : "border-zinc-300 focus:ring-zinc-900",
  );

type ChangeColumn = (key: string, patch: Partial<ColumnDraft>) => void;

type SchemaColumnRowProps = {
  draft: ColumnDraft;
  number: number;
  errors: ColumnDraftErrors | undefined;
  disabled: boolean;
  canRemove: boolean;
  autoFocus: boolean;
  onChange: ChangeColumn;
  onRemove: (key: string) => void;
};

const SchemaColumnRow = ({
  draft,
  number,
  errors,
  disabled,
  canRemove,
  autoFocus,
  onChange,
  onRemove,
}: SchemaColumnRowProps) => {
  const id = useId();
  const name = `column ${number}`;
  const types: CurationValueType[] =
    draft.original?.value_type === "figure" ? [...COLUMN_TYPES, "figure"] : COLUMN_TYPES;

  return (
    <tr className="border-t border-zinc-200 align-top">
      <td className="px-3 py-3">
        <Input
          aria-label={`Label of ${name}`}
          aria-describedby={errors?.label ? `${id}-label-error` : undefined}
          error={Boolean(errors?.label)}
          value={draft.label}
          maxLength={MAX_LABEL_LENGTH}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={(event) => onChange(draft.key, { label: event.target.value })}
          className="min-w-44 text-sm"
        />
        {errors?.label && (
          <p id={`${id}-label-error`} className="mt-1 text-xs text-red-700">
            {errors.label}
          </p>
        )}
      </td>
      <td className="px-3 py-3">
        <select
          aria-label={`Type of ${name}`}
          value={draft.valueType}
          disabled={disabled}
          onChange={(event) =>
            onChange(draft.key, { valueType: event.target.value as CurationValueType })
          }
          className={SELECT_CLASS}
        >
          {types.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </td>
      <td className="space-y-2 px-3 py-3">
        <label className="block text-xs font-medium text-zinc-600">
          Description
          <textarea
            aria-label={`Description of ${name}`}
            value={draft.description}
            rows={2}
            maxLength={MAX_NOTE_LENGTH}
            disabled={disabled}
            onChange={(event) => onChange(draft.key, { description: event.target.value })}
            className={textareaClass()}
          />
        </label>
        <label className="block text-xs font-medium text-zinc-600">
          Normalization rules
          <textarea
            aria-label={`Normalization rules of ${name}`}
            value={draft.normalization}
            rows={2}
            maxLength={MAX_NOTE_LENGTH}
            disabled={disabled}
            placeholder="Optional"
            onChange={(event) => onChange(draft.key, { normalization: event.target.value })}
            className={textareaClass()}
          />
        </label>
        <label className="block text-xs font-medium text-zinc-600">
          Examples, one per line
          <textarea
            aria-label={`Examples of ${name}`}
            aria-invalid={errors?.examples ? true : undefined}
            aria-describedby={errors?.examples ? `${id}-examples-error` : undefined}
            value={draft.examplesText}
            rows={2}
            disabled={disabled}
            placeholder="Optional"
            onChange={(event) => onChange(draft.key, { examplesText: event.target.value })}
            className={textareaClass(Boolean(errors?.examples))}
          />
        </label>
        {errors?.examples && (
          <p id={`${id}-examples-error`} className="text-xs text-red-700">
            {errors.examples}
          </p>
        )}
        {draft.valueType === "categorical" && (
          <>
            <label className="block text-xs font-medium text-zinc-600">
              Options, one per line
              <textarea
                aria-label={`Options of ${name}`}
                aria-invalid={errors?.options ? true : undefined}
                aria-describedby={errors?.options ? `${id}-options-error` : undefined}
                value={draft.optionsText}
                rows={3}
                disabled={disabled}
                placeholder="Optional. A cell must then be one of these values."
                onChange={(event) => onChange(draft.key, { optionsText: event.target.value })}
                className={textareaClass(Boolean(errors?.options))}
              />
            </label>
            {errors?.options && (
              <p id={`${id}-options-error`} className="text-xs text-red-700">
                {errors.options}
              </p>
            )}
          </>
        )}
      </td>
      <td className="px-3 py-3">
        <select
          aria-label={`Required for ${name}`}
          value={draft.required ? "yes" : "no"}
          disabled={disabled}
          onChange={(event) => onChange(draft.key, { required: event.target.value === "yes" })}
          className={SELECT_CLASS}
        >
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </td>
      <td className="px-3 py-3">
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Remove ${name}`}
          title="Remove column"
          disabled={disabled || !canRemove}
          onClick={() => onRemove(draft.key)}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      </td>
    </tr>
  );
};

type SchemaColumnsEditorProps = {
  drafts: ColumnDraft[];
  errors: Record<string, ColumnDraftErrors>;
  disabled: boolean;
  focusKey: string | null;
  onChange: ChangeColumn;
  onRemove: (key: string) => void;
  onAdd: () => void;
};

export const SchemaColumnsEditor = ({
  drafts,
  errors,
  disabled,
  focusKey,
  onChange,
  onRemove,
  onAdd,
}: SchemaColumnsEditorProps) => (
  <div className="space-y-3">
    <div className="overflow-x-auto rounded-[3px] border border-zinc-200">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-zinc-50 text-xs text-zinc-500">
          <tr>
            <th scope="col" className="px-3 py-3 font-medium">Label</th>
            <th scope="col" className="px-3 py-3 font-medium">Type</th>
            <th scope="col" className="px-3 py-3 font-medium">Description</th>
            <th scope="col" className="px-3 py-3 font-medium">Required</th>
            <th scope="col" className="px-3 py-3 font-medium">
              <span className="sr-only">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {drafts.map((draft, index) => (
            <SchemaColumnRow
              key={draft.key}
              draft={draft}
              number={index + 1}
              errors={errors[draft.key]}
              disabled={disabled}
              canRemove={drafts.length > 1}
              autoFocus={draft.key === focusKey}
              onChange={onChange}
              onRemove={onRemove}
            />
          ))}
        </tbody>
      </table>
    </div>
    <Button
      variant="outline"
      size="sm"
      onClick={onAdd}
      disabled={disabled || drafts.length >= MAX_COLUMNS}
    >
      <Plus className="h-4 w-4" aria-hidden />
      Add column
    </Button>
  </div>
);
